import { p, h, ul, code, tip, info, warn, table, cards } from './helpers';

const course = {
  id: 'config-language',
  step: 4,
  title: 'The kamailio.cfg Language',
  tagline: 'Global parameters, modules, routing blocks, pseudo-variables, transformations and control flow.',
  icon: 'Code2',
  colors: ['#a3ff3d', '#00c48c'],
  level: 'Intermediate',
  lessons: [
    {
      id: 'file-structure',
      title: 'Structure of kamailio.cfg',
      icon: 'FileCode2',
      minutes: 9,
      summary: 'Three sections in a fixed order, plus a preprocessor that switches features on and off.',
      blocks: [
        p('Every Kamailio config has three sections, in this order:'),
        cards([
          { icon: 'SlidersHorizontal', title: '1. Global parameters', text: 'Core settings: log level, listen sockets, number of processes, aliases.' },
          { icon: 'Puzzle', title: '2. Modules', text: '`loadmodule` lines and the `modparam` settings for each module.' },
          { icon: 'GitBranch', title: '3. Routing logic', text: 'The script blocks that run for each request and reply: `request_route`, `route[...]`, `reply_route` and others.' },
        ]),
        h('The preprocessor'),
        p('Lines starting with `#!` are preprocessor directives, evaluated before the config is parsed. They let one file serve many deployments. A plain `#` starts a comment, and `//` and `/* ... */` comments also work.'),
        code('cfg', `
#!KAMAILIO
#
# Turn features on by defining them:
#!define WITH_MYSQL
#!define WITH_AUTH
#!define WITH_USRLOCDB
#!define WITH_NAT

#!ifdef WITH_MYSQL
# a define can carry a value, used later like a constant
#!ifndef DBURL
#!define DBURL "mysql://kamailio:kamailiorw@localhost/kamailio"
#!endif
#!endif

# substitute text everywhere in the file, including inside strings
#!substdef "!MY_IP!203.0.113.10!g"

# split long configs into files
include_file "/etc/kamailio/kamailio-local.cfg"
import_file  "/etc/kamailio/optional.cfg"   # no error if missing`),
        table(['Directive', 'Meaning'], [
          ['`#!define NAME [value]`', 'Defines a name, optionally with a value'],
          ['`#!ifdef` / `#!ifndef` / `#!else` / `#!endif`', 'Conditional blocks'],
          ['`#!subst` / `#!substdef`', 'Regex text substitution; `substdef` also defines the name'],
          ['`include_file` / `import_file`', 'Include another file; `import_file` ignores missing files'],
          ['`#!trydef` / `#!redefine`', 'Define only if not already defined / force redefinition'],
        ]),
        tip('Define from the command line', 'You can toggle features without editing the file: `kamailio -A WITH_DEBUG -f kamailio.cfg`. The default config is built entirely around these switches.'),
      ],
    },
    {
      id: 'globals-and-modules',
      title: 'Global parameters and modules',
      icon: 'Settings2',
      minutes: 10,
      summary: 'Listening sockets, process counts, module loading and module parameters.',
      blocks: [
        code('cfg', `
####### Global Parameters #########
debug=2                      # log level: 2 = INFO, 3 = DEBUG
log_stderror=no
log_facility=LOG_LOCAL0
log_prefix="{$mt $hdr(CSeq) $ci} "

memdbg=5
memlog=5

children=8                   # UDP worker processes per socket
tcp_children=4
tcp_connection_lifetime=3605 # slightly longer than registration expiry

listen=udp:203.0.113.10:5060
listen=tcp:203.0.113.10:5060
listen=udp:10.0.0.10:5060 advertise 203.0.113.10:5060

alias="example.com"
alias="sip.example.com"

dns=no
rev_dns=no
auto_aliases=no
user_agent_header="User-Agent: Kamailio"
server_header="Server: Kamailio"`),
        info('Log levels', 'Log levels are `-5` (L_ALERT) up through `-1` (L_ERR), `0` (L_WARN), `1` (L_NOTICE), `2` (L_INFO) and `3` (L_DBG). With `debug=2`, everything at INFO and more severe is logged. Newer releases also accept the name `log_level` for the same setting.'),
        h('Listen and advertise'),
        p('`listen` binds a socket as `proto:address:port`. When Kamailio runs on a private IP behind 1:1 NAT (common in clouds), `advertise` tells it which public address to write into Via and Record-Route headers.'),
        h('Loading modules'),
        code('cfg', `
####### Modules Section ########
mpath="/usr/lib/x86_64-linux-gnu/kamailio/modules/"

loadmodule "tm.so"
loadmodule "sl.so"
loadmodule "rr.so"
loadmodule "pv.so"
loadmodule "usrloc.so"
loadmodule "registrar.so"

# modparam("module", "parameter", value)
modparam("tm", "fr_timer", 5000)          # non-INVITE timeout, ms
modparam("tm", "fr_inv_timer", 120000)    # max ringing time, ms
modparam("rr", "enable_full_lr", 0)
modparam("rr", "append_fromtag", 0)
modparam("usrloc", "db_mode", 0)          # 0 = memory only
modparam("registrar", "max_expires", 3600)
modparam("registrar", "min_expires", 60)`),
        ul([
          'Values are integers or double-quoted strings. Some modules accept a parameter several times, for example `modparam("htable", "htable", ...)` once per table.',
          'A parameter can be set for several modules at once: `modparam("nathelper|registrar", "received_avp", "$avp(RECEIVED)")`.',
          'Every module has a README on kamailio.org listing its parameters, functions, RPC commands and statistics. Bookmark the modules index for your version.',
        ]),
        info('Order matters a little', 'Load a module before any `modparam` that refers to it, and load database drivers such as `db_mysql.so` before modules that use a database.'),
      ],
    },
    {
      id: 'routing-blocks',
      title: 'Routing blocks',
      icon: 'Split',
      minutes: 12,
      summary: 'Which block runs for which message, and how they hand off to each other.',
      blocks: [
        table(['Block', 'Runs for', 'Typical use'], [
          ['`request_route { }`', 'Every SIP request received', 'Main entry point. Checks, auth, registration, routing decisions.'],
          ['`route[NAME] { }`', 'When called with `route(NAME)`', 'Sub-routines to organize the script.'],
          ['`branch_route[NAME] { }`', 'Each outgoing branch of a transaction, armed with `t_on_branch()`', 'Per-destination changes: add headers, NAT handling per branch.'],
          ['`onreply_route[NAME] { }`', 'Replies of a transaction, armed with `t_on_reply()`', 'Inspect or fix replies, e.g. NAT handling of 200 OK SDP.'],
          ['`failure_route[NAME] { }`', 'When a transaction ends with a negative final reply or timeout, armed with `t_on_failure()`', 'Serial forking: voicemail on busy, next gateway on 503.'],
          ['`reply_route { }`', 'Every reply received, before transaction matching', 'Global reply checks, dropping junk replies.'],
          ['`onsend_route { }`', 'Just before a request is sent out', 'Last-moment checks on the destination address.'],
          ['`event_route[mod:event] { }`', 'Events raised by the core or modules', '`tm:local-request`, `xhttp:request`, `dispatcher:dst-down`, `htable:mod-init`, and many more.'],
        ]),
        h('How they connect'),
        code('cfg', `
request_route {
    route(REQINIT);               # call a sub-route
    ...
    t_on_branch("MANAGE_BRANCH"); # arm callbacks for this transaction
    t_on_reply("MANAGE_REPLY");
    t_on_failure("MANAGE_FAILURE");
    t_relay();
    exit;
}

route[REQINIT] {
    if (!mf_process_maxfwd_header("10")) {
        sl_send_reply("483", "Too Many Hops");
        exit;                     # stop the whole script
    }
    return;                       # back to the caller
}

branch_route[MANAGE_BRANCH] {
    xlog("L_DBG", "new branch [$T_branch_idx] to $ru\\n");
}

onreply_route[MANAGE_REPLY] {
    if (status =~ "[12][0-9][0-9]") {
        xlog("L_INFO", "reply $rs $rr\\n");
    }
}

failure_route[MANAGE_FAILURE] {
    if (t_is_canceled()) exit;
    if (t_check_status("486|408")) {
        $ru = "sip:voicemail@10.0.0.30";
        t_relay();
    }
}`),
        h('exit, return and drop'),
        ul([
          '`exit;` stops processing the current message entirely. In `request_route`, a request you have not relayed or answered is simply dropped.',
          '`return;` leaves the current sub-route and continues in the caller. `return(1)` or `return(-1)` set a return code.',
          '`drop;` like exit, but in a branch_route it also discards that branch, and in onreply_route it stops the reply from being forwarded (for provisional replies).',
        ]),
        warn('Not every function works everywhere', 'Each module function lists the route types where it may be used. For example `sl_send_reply()` is not allowed in `onreply_route`. Kamailio refuses to start if you use a function in the wrong place, which is another reason to run `kamailio -c`.'),
      ],
    },
    {
      id: 'pseudo-variables',
      title: 'Pseudo-variables and transformations',
      icon: 'Variable',
      minutes: 13,
      summary: 'The $variables that read and write the SIP message, and the {transformations} that reshape them.',
      blocks: [
        p('Pseudo-variables (PVs) start with `$` and give the script access to the message, the transaction and to its own storage. Some are read-only, many can be assigned.'),
        h('Message fields'),
        table(['PV', 'Value', 'Writable'], [
          ['`$rm`', 'Request method', 'no'],
          ['`$ru`', 'Request-URI', 'yes'],
          ['`$rU` / `$rd` / `$rp`', 'R-URI user / domain / port', 'yes'],
          ['`$du`', 'Destination URI (next hop)', 'yes'],
          ['`$fu` / `$fU` / `$fd`', 'From URI / user / domain', 'yes'],
          ['`$tu` / `$tU` / `$td`', 'To URI / user / domain', 'yes'],
          ['`$fn` / `$tn`', 'From / To display name', 'yes'],
          ['`$ft` / `$tt`', 'From tag / To tag', 'no'],
          ['`$ci`', 'Call-ID', 'no'],
          ['`$cs`', 'CSeq number', 'no'],
          ['`$ua`', 'User-Agent header', 'no'],
          ['`$hdr(Name)`', 'Body of the first header called Name', 'no (use textops to change)'],
          ['`$rb`', 'Message body', 'yes'],
          ['`$rs` / `$rr`', 'Reply status code / reason (in reply routes)', 'no'],
          ['`$si` / `$sp`', 'Source IP / port of the packet', 'no'],
          ['`$Ri` / `$Rp`', 'Local IP / port where the packet was received', 'no'],
          ['`$pr` / `$proto`', 'Transport protocol of the received message', 'no'],
          ['`$T_reply_code`', 'Winning reply code, in failure_route', 'no'],
          ['`$au`', 'Username from the Authorization header', 'no'],
        ]),
        h('Script storage'),
        table(['PV', 'Lives in', 'Lifetime'], [
          ['`$var(x)`', 'Private memory', 'Current process. Not reset between messages, so always initialize it.'],
          ['`$avp(x)`', 'Shared memory, attached to the transaction', 'Follows the transaction into reply and failure routes.'],
          ['`$xavp(r=>f)`', 'Shared memory, structured', 'Like AVPs, with named fields.'],
          ['`$shv(x)`', 'Shared memory, global', 'Until restart. Visible to all processes.'],
          ['`$sht(table=>key)`', 'htable module', 'Global hash table entry, optionally auto-expiring.'],
          ['`$dlg_var(x)`', 'dialog module', 'Lives as long as the call.'],
        ]),
        code('cfg', `
$var(count) = 0;                       # private, reset it yourself
$avp(caller) = $fU;                    # available in failure_route later
$shv(maintenance) = 1;                 # global flag for all processes
$sht(ipban=>$si) = 1;                  # ban this IP (htable)

if ($ua =~ "friendly-scanner|sipvicious") {
    xlog("L_WARN", "scanner from $si, dropping\\n");
    exit;
}`),
        h('Transformations'),
        p('Transformations are applied inside `$( ... )` with one or more `{class.name}` suffixes. They never modify the original value.'),
        code('cfg', `
$var(user)   = $(ru{uri.user});           # user part of a URI
$var(len)    = $(rU{s.len});              # string length
$var(prefix) = $(rU{s.substr,0,3});       # first three characters
$var(lower)  = $(fd{s.tolower});
$var(num)    = $(hdr(X-Account){s.int});  # convert to integer
$var(name)   = $(hdr(From){tobody.display});
$var(tag)    = $(hdr(Via){param.value,branch});
$var(b64)    = $(var(secret){s.encode.base64});

# chain them
xlog("L_INFO", "dialed prefix: $(ru{uri.user}{s.substr,0,4})\\n");`),
        tip('Escaping in strings', 'Inside double-quoted strings, PVs are expanded by functions that accept them (such as `xlog`). Write `\\n` for a newline and `$$` for a literal dollar sign.'),
      ],
    },
    {
      id: 'control-flow',
      title: 'Operators, control flow and flags',
      icon: 'GitCompareArrows',
      minutes: 11,
      summary: 'if, switch and while, return-code rules that surprise everybody, and message flags.',
      blocks: [
        h('Conditions and operators'),
        code('cfg', `
if (is_method("INVITE") && $rU =~ "^\\+?1[0-9]{10}$") {
    route(PSTN);
} else if ($rU == "*97") {
    route(VOICEMAIL);
}

if (src_ip == 10.0.0.0/8 || $si == "192.0.2.50") { ... }
if (uri == myself)  { ... }      # R-URI points to one of our domains
if (from_uri == myself) { ... }  # caller claims to be local
if ($var(n) >= 5 && !isflagset(FLT_NATS)) { ... }

switch ($rm) {
    case "REGISTER":
        route(REGISTRAR);
        break;
    case "INVITE":
    case "MESSAGE":
        route(LOCATION);
        break;
    default:
        sl_send_reply("405", "Method Not Allowed");
        exit;
}

$var(i) = 0;
while ($var(i) < 3) {
    xlog("L_INFO", "iteration $var(i)\\n");
    $var(i) = $var(i) + 1;
}`),
        table(['Operator', 'Meaning'], [
          ['`==`, `!=`', 'Equal, not equal (string or integer)'],
          ['`=~`, `!~`', 'Matches / does not match a POSIX regular expression'],
          ['`>`, `<`, `>=`, `<=`', 'Integer comparisons'],
          ['`&&`, `||`, `!`', 'Logical and, or, not'],
          ['`+ - * / mod`', 'Integer arithmetic'],
          ['`+`', 'Also string concatenation when the operands are strings'],
        ]),
        h('Return codes: the rule that bites everyone'),
        ul([
          'A **positive** return value means **true**.',
          'A **negative** return value means **false**.',
          'A return value of **0** means **exit**: the script stops right there.',
        ]),
        p('`$rc` (or `$retcode`) holds the return value of the last function or route. Module functions are designed never to return 0 unless they want the script to stop, but your own sub-routes can. A `return(0);` in a `route[]` silently ends processing of the whole message.'),
        warn('Write return(1) or return(-1)', 'When a sub-route is used in a condition, like `if (route(CHECK_IP))`, return `1` for true and `-1` for false. Never `0`.'),
        h('Flags'),
        table(['Kind', 'Functions', 'Scope'], [
          ['Message / transaction flags', '`setflag(n)`, `resetflag(n)`, `isflagset(n)`', 'The request and its transaction. Visible in reply and failure routes.'],
          ['Branch flags', '`setbflag(n)`, `resetbflag(n)`, `isbflagset(n)`', 'One branch. Saved with the contact in usrloc, so they survive into later calls.'],
          ['Script flags', '`setsflag(n)`, `resetsflag(n)`, `issflagset(n)`', 'Current script execution only.'],
        ]),
        code('cfg', `
#!define FLT_ACC 1        # account this call
#!define FLT_NATS 5       # caller is behind NAT
#!define FLB_NATB 6       # callee branch is behind NAT
#!define FLB_NATSIPPING 7 # send OPTIONS keepalives to this contact

if (nat_uac_test("19")) setflag(FLT_NATS);`),
        h('Logging with xlog'),
        code('cfg', `
xlog("L_INFO",  "$rm from $fu ($si:$sp) to $ru\\n");
xlog("L_ERR",   "auth failed for $au from $si\\n");
xdbg("only printed when debug >= 3: $ci\\n");`),
        info('Levels', 'xlog levels are `L_ALERT`, `L_BUG`, `L_CRIT`, `L_ERR`, `L_WARN`, `L_NOTICE`, `L_INFO` and `L_DBG`. A message is printed only when the global `debug` level is high enough.'),
      ],
    },
  ],
  quiz: [
    { q: 'In which order must the sections of kamailio.cfg appear?', options: ['Routing, modules, globals', 'Globals, modules, routing', 'Modules, globals, routing', 'Any order'], answer: 1, explain: 'Global parameters come first, then loadmodule and modparam lines, then the routing blocks.' },
    { q: 'A sub-route executes return(0). What happens?', options: ['It returns false to the caller', 'It returns true to the caller', 'The script stops processing the message', 'Kamailio restarts'], answer: 2, explain: 'A return code of 0 means exit. Use 1 for true and -1 for false.' },
    { q: 'Which block runs when a forwarded INVITE fails with 486 Busy Here, if you armed it?', options: ['branch_route', 'onreply_route', 'failure_route', 'onsend_route'], answer: 2, explain: 'failure_route, armed with t_on_failure(), runs when the transaction ends with a negative reply or timeout.' },
    { q: 'Which pseudo-variable holds the source IP address of the received packet?', options: ['$ru', '$fd', '$si', '$Ri'], answer: 2, explain: '$si is the source IP. $Ri is the local address that received the packet.' },
    { q: 'Which storage follows a transaction into its failure_route?', options: ['$var(x)', '$avp(x)', 'A local shell variable', 'None of them'], answer: 1, explain: 'AVPs are attached to the transaction in shared memory, so they are still available in reply and failure routes.' },
  ],
};

export default course;
